'use client';

import { doc, FieldPath, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import {
  normalizeDataExplorerBrandFavorites,
  type DataExplorerBrandFavorite,
} from '@/lib/attune/data-explorer';
import { db } from '@/lib/firebase';

/** Optimistic, project-scoped saved links. Saving never holds up navigation. */
export function useDataExplorerBrandFavorites(projectId?: string) {
  const { user, userDocument } = useAuth();
  const favoritesReady = Boolean(user && projectId && userDocument);
  const key = user && projectId ? `attune:brand-favorites:${user.uid}:${projectId}` : '';
  const profile = userDocument?.preferences?.data_explorer_brand_favorites?.[projectId || ''];
  const confirmed = useMemo(() => normalizeDataExplorerBrandFavorites(profile), [profile]);
  const [local, setLocal] = useState<{
    key: string;
    favorites: DataExplorerBrandFavorite[];
  } | null>(null);
  const favorites = local?.key === key ? local.favorites : confirmed;
  const current = useRef({ key, favorites });
  current.current = { key, favorites };
  const writes = useRef(Promise.resolve());

  useEffect(() => {
    if (!key) return;
    try {
      const stored = window.localStorage.getItem(key);
      if (stored)
        setLocal({ key, favorites: normalizeDataExplorerBrandFavorites(JSON.parse(stored)) });
    } catch {
      /* Storage may be unavailable. Profile favorites still work. */
    }
  }, [key]);

  const toggleFavorite = useCallback(
    (brand: DataExplorerBrandFavorite) => {
      if (!user || !projectId || !key || !favoritesReady) return;
      const previous = current.current.key === key ? current.current.favorites : confirmed;
      const next = normalizeDataExplorerBrandFavorites(
        previous.some(item => item.slug === brand.slug)
          ? previous.filter(item => item.slug !== brand.slug)
          : [...previous, brand]
      );
      current.current = { key, favorites: next };
      setLocal({ key, favorites: next });
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* Profile sync remains available. */
      }
      // Serialize rapid star clicks so a slower earlier save cannot overwrite a newer choice.
      writes.current = writes.current
        .catch(() => undefined)
        .then(async () => {
          await updateDoc(
            doc(db, 'users', user.uid),
            new FieldPath('preferences', 'data_explorer_brand_favorites', projectId),
            next,
            'updated_at',
            serverTimestamp()
          );
          try {
            if (window.localStorage.getItem(key) === JSON.stringify(next)) {
              window.localStorage.removeItem(key);
            }
          } catch {
            /* Optional local fallback. */
          }
        })
        .catch(() => {
          toast.error('Brand favorites saved locally; profile sync failed');
        });
    },
    [confirmed, favoritesReady, key, projectId, user]
  );

  return { favorites, favoritesReady, toggleFavorite };
}
